import 'src/global.css';
import '@fontsource-variable/inter';

import { useEffect, useMemo } from 'react';

import { usePathname } from 'src/routes/hooks';

import { themeConfig, ThemeProvider } from 'src/theme';
import { I18nProvider } from 'src/locales/i18n-provider';

import { ProgressBar } from 'src/components/progress-bar';
import { MotionLazy } from 'src/components/animate/motion-lazy';
import { SettingsDrawer, defaultSettings, SettingsProvider } from 'src/components/settings';

import { AuthProvider, useAuth } from 'react-oidc-context';
import { LocalizationProvider, useTranslate } from './locales';
import { Snackbar } from 'src/components/snackbar/snackbar';
import { Provider } from 'react-redux';
import { store } from './store/store';
import { ErrorBoundary } from './ErrorBoundary';
import { Box, Button, Typography } from '@mui/material';

// ----------------------------------------------------------------------

type AppProps = {
  children: React.ReactNode;
};

function InterceptOidcError({ children }: { children: React.ReactNode }) {
  const auth = useAuth();

  const { t } = useTranslate();

  if (auth?.error?.message?.includes('No matching state found in storage')) {
    return (
      <Box>
        <Typography>{t('common.tokenHasExpired')}</Typography>
        <Button
          variant="contained"
          size="small"
          sx={{ mt: 2 }}
          onClick={() => window.location.reload()}
        >
          {t('common.goBack')}
        </Button>
      </Box>
    );
  }

  return <>{children}</>;
}

export default function App({ children }: AppProps) {
  const oidcConfig = useMemo(() => {
    const config = {
      authority: `${window.RUNTIME_CONFIG.KEYCLOAK_AUTHORITY}/${window.RUNTIME_CONFIG.KEYCLOAK_REALM}`,
      client_id: window.RUNTIME_CONFIG.KEYCLOAK_CLIENT,
      redirect_uri: window.RUNTIME_CONFIG.KEYCLOAK_REDIRECT_URI,
      response_type: 'code',
      scope: 'openid profile email',
      automaticSilentRenew: true,
    };

    window.OIDC_CONFIG = config;
    return config;
  }, []);

  useScrollToTop();

  return (
    <I18nProvider>
      <Provider store={store}>
        <AuthProvider {...oidcConfig}>
          <InterceptOidcError>
            <SettingsProvider defaultSettings={defaultSettings}>
              <LocalizationProvider>
                <ThemeProvider
                  noSsr
                  defaultMode={themeConfig.defaultMode}
                  modeStorageKey={themeConfig.modeStorageKey}
                >
                  <ErrorBoundary>
                    <MotionLazy>
                      <Snackbar />
                      <ProgressBar />
                      <SettingsDrawer defaultSettings={defaultSettings} />
                      {children}
                    </MotionLazy>
                  </ErrorBoundary>
                </ThemeProvider>
              </LocalizationProvider>
            </SettingsProvider>
          </InterceptOidcError>
        </AuthProvider>
      </Provider>
    </I18nProvider>
  );
}

// ----------------------------------------------------------------------

function useScrollToTop() {
  const pathname = usePathname();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}
